function InvoiceAssistantApproval() {
  const control = React.useContext(SuperPlummaContext);
  const approval = control?.state.approval;
  if (approval?.presentation !== 'invoice_review') return null;
  return <InvoiceAssistantApprovalForm key={approval.id} approval={approval} session={control.session} />;
}

function InvoiceAssistantApprovalForm({ approval, session }) {
  const confirming = useRef(false);
  return <section className="invoice-assistant-review panel" data-assistant-exclude="true" aria-label={t('live.reviewTitle')}>
    <strong>{t('live.reviewTitle')}</strong>
    <p className="t-sm muted">{t('live.reviewInvoice')}</p>
    <div className="row wrap gap-8">
      <Button icon="check" onClick={event => {
        if (confirming.current || event.nativeEvent?.isTrusted !== true) return;
        confirming.current = session.decideApproval(approval.id, true, event.nativeEvent);
      }}>{t('live.approveIssue')}</Button>
      <Button variant="ghost" onClick={() => session.stop()}>{t('common.cancel')}</Button>
    </div>
  </section>;
}
Object.assign(window, { InvoiceAssistantApproval });

// Local actions are reviewed on their owning page; only the decision lives here.
function PageActionApproval({ approval, onClose, onConfirm }) {
  const submitting = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const key = `actionApproval.label.${approval.action}`;
  const label = t(key);
  const confirmLabel = label === key ? t('actionApproval.approve') : t('actionApproval.approveAction', { action: label });
  return <section className="assistant-page-approval" data-assistant-exclude="true" aria-label={t('actionApproval.title')}>
    {error && <p role="alert" className="invoice-control-error">{error}</p>}
    <div className="row wrap gap-8">
      <Button disabled={busy} icon="check" onClick={async event => {
        if (event.nativeEvent?.isTrusted !== true || submitting.current) return;
        submitting.current = true; setBusy(true); setError('');
        try {
          if (await onConfirm(event.nativeEvent) === false) { submitting.current = false; setBusy(false); }
        } catch {
          submitting.current = false; setBusy(false); setError(t('actionApproval.failed'));
        }
      }}>{confirmLabel}</Button>
      <Button variant="ghost" disabled={busy} onClick={() => { if (!submitting.current) onClose(); }}>{t('common.cancel')}</Button>
    </div>
  </section>;
}
Object.assign(window, { PageActionApproval });

function AssistantActionApproval({ approval, onClose, onConfirm, busy=false, error='' }) {
  const trusted = useRef(null);
  const confirmed = useRef(false);
  const label = key => {
    const translated = t(`actionApproval.label.${key}`);
    return translated === `actionApproval.label.${key}` ? key.replace(/([a-z])([A-Z])/g,'$1 $2').replace(/_/g,' ') : translated;
  };
  const detail = (value, key='') => {
    if (value == null) return '';
    if (typeof value === 'number') return PlummeI18n.number(value, {maximumFractionDigits:8});
    if (typeof value === 'string' && ['date','invoiceDate','dueDate'].includes(key) && /^\d{4}-\d{2}-\d{2}$/.test(value)) return PlummeI18n.date(value, {day:'2-digit',month:'2-digit',year:'numeric'});
    if (typeof value !== 'object') return String(value);
    return Object.entries(value).filter(([key]) => !['authorizationQuote','observationId','controlId','version','key','id','source','createdAt','updatedAt','capabilityVersion','businessId','requestId','status','action','accessHash','inputHash','stateHash'].includes(key))
      .map(([key,item]) => `${/^\d+$/.test(key) ? Number(key)+1 : label(key)}: ${detail(item,key)}`).join('\n');
  };
  return <div data-assistant-exclude="true" onClickCapture={event => {
    trusted.current = event.nativeEvent;
    if (!event.nativeEvent.isTrusted) { event.preventDefault(); event.stopPropagation(); }
  }}>
    <ConfirmModal title={t('actionApproval.title')} danger={false} busy={busy} max={620}
      onClose={() => { if (!confirmed.current) onClose(); }} confirmLabel={t('actionApproval.confirm')} cancelLabel={t('actionApproval.cancel')}
      onConfirm={async () => { if (trusted.current?.isTrusted !== true) throw new Error(t('actionApproval.failed')); await onConfirm(trusted.current); confirmed.current = true; }}>
      <span style={{display:'block'}}>{t('actionApproval.body')}</span>
      <strong style={{display:'block',marginTop:16}}>{label(approval.action)}</strong>
      <span style={{display:'block',whiteSpace:'pre-wrap',overflowWrap:'anywhere',maxHeight:'45vh',overflow:'auto',marginTop:12}}>
        {detail(approval.step)}{approval.surface ? `\n\n${t('actionApproval.context')}\n${detail(approval.surface)}` : ''}
      </span>
      {error && <span role="alert" style={{display:'block',color:'var(--danger)',marginTop:12}}>{error}</span>}
    </ConfirmModal>
  </div>;
}
Object.assign(window, { AssistantActionApproval });

function McpActionApproval({ businessId }) {
  const [command, setCommand] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const params = new URL(window.location.href).searchParams;
  const commandId = params.get('approveAction');
  const requestedBusiness = params.get('approvalBusiness');
  useEffect(() => {
    if (!commandId || requestedBusiness !== businessId) return;
    let active = true;
    window.PlummeBackend.mcpActionApproval({ action:'get', businessId, commandId }).then(result => {
      if (active) setCommand(result.command);
    }).catch(() => { if (active) setError(t('actionApproval.failed')); });
    return () => { active = false; };
  }, [businessId, commandId, requestedBusiness]);
  const close = () => {
    if (busy) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('approveAction'); url.searchParams.delete('approvalBusiness');
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    setCommand(null); setError('');
  };
  if (!commandId) return null;
  if (requestedBusiness !== businessId || !command || command.status !== 'awaiting_approval') return <div data-assistant-exclude="true"><Modal open onClose={close} title={t('actionApproval.mcp')}>
    <p>{requestedBusiness !== businessId ? `${t('actionApproval.business')}: ${requestedBusiness}` : error || (command?.status === 'completed' ? t('actionApproval.done') : !command || command.status === 'executing' ? t('actionApproval.loading') : t('actionApproval.failed'))}</p>
  </Modal></div>;
  return <AssistantActionApproval key={command.commandId} busy={busy} error={error} onClose={async () => {
    if (busy) return;
    setBusy(true);
    try { await window.PlummeBackend.mcpActionApproval({ action:'cancel', businessId, commandId }); close(); }
    catch { setError(t('actionApproval.failed')); }
    finally { setBusy(false); }
  }}
    approval={{ action:command.capabilityName, step:command.input, surface:{ business:command.proposal?.businessName, ...command.proposal?.review, expires:command.expiresAt } }}
    onConfirm={async event => {
      if (!event.isTrusted || busy || window.PlummeBackend.activeBusinessId !== businessId) throw new Error(t('actionApproval.failed'));
      setBusy(true); setError('');
      try {
        const result = await window.PlummeBackend.mcpActionApproval({ action:'approve', businessId, commandId, inputHash:command.inputHash });
        setCommand(result.command);
        if (result.command?.status === 'completed') toast(t('actionApproval.done'));
      } catch (failure) { setError(t('actionApproval.failed')); throw failure; }
      finally { setBusy(false); }
    }} />;
}
Object.assign(window, { McpActionApproval });
